import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const YESNO = ["", "Yes", "No"];
const TYPES = [["introductory", "Introductory (first visit)"], ["provisional", "Provisional hire"], ["random", "Random observation"]];

const EMPTY_FORM = { face_to_face_done: "", is_provisional: "", shadow_or_orientation: "", shadow_details: "",
  client_feedback: "", client_feedback_details: "", notes: "", observation_type: "introductory", push_to_employee: true };

function ObservationForm({ target, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((cur) => ({ ...cur, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/observations", { client_id: target.client_id, caregiver_name: target.caregiver_name,
        first_visit_date: target.first_visit_date, ...f });
      toast.success("Observation recorded");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const Sel = ({ k, label, options = YESNO }) => (
    <div>
      <label className="text-xs font-medium text-stone-600">{label}</label>
      <select value={f[k]} onChange={set(k)} className="mt-1 h-9 w-full rounded-lg border border-stone-300 bg-white px-2 text-sm" data-testid={`obs-${k}`}>
        {options.map((o) => Array.isArray(o)
          ? <option key={o[0]} value={o[0]}>{o[1]}</option>
          : <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </div>
  );

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-[#2F6B4F]/25 bg-[#2F6B4F]/[0.04] p-4" data-testid="observation-form">
      {(target.first_visits || []).length > 0 && (
        <div className="rounded-md border border-stone-200 bg-white p-2.5" data-testid="obs-first-visits">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">First visits on record — {target.caregiver_name}</p>
          <p className="mt-1 text-xs text-stone-700">{target.first_visits.map((d) => <span key={d} className="mr-3 inline-block rounded bg-stone-100 px-2 py-0.5 font-medium">{d}</span>)}</p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Sel k="observation_type" label="Observation type" options={TYPES} />
        <Sel k="is_provisional" label="Is the employee provisionally hired?" />
        <Sel k="face_to_face_done" label="Did we do an introductory face-to-face?" />
        <Sel k="shadow_or_orientation" label="Shadowed another employee, or intro/orientation done another day?" />
      </div>
      {(f.shadow_or_orientation === "Yes") && (
        <div>
          <label className="text-xs font-medium text-stone-600">Shadow / orientation details (who, when, where)</label>
          <textarea value={f.shadow_details} onChange={set("shadow_details")} rows={2}
            className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="obs-shadow-details" />
        </div>
      )}
      {f.face_to_face_done === "No" && (
        <div className="rounded-lg border border-[#C49A3A]/40 bg-[#C49A3A]/[0.06] p-3" data-testid="obs-no-f2f">
          <Sel k="client_feedback" label="No face-to-face — did we get direct feedback from the client?" />
          {f.client_feedback === "Yes" && (
            <div className="mt-2">
              <label className="text-xs font-medium text-stone-600">Client feedback details</label>
              <textarea value={f.client_feedback_details} onChange={set("client_feedback_details")} rows={2}
                className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="obs-feedback-details" />
            </div>
          )}
          {f.client_feedback === "No" && (
            <p className="mt-2 text-xs font-medium text-[#8a5d14]">No face-to-face and no client feedback — schedule the observation as soon as possible.</p>
          )}
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-stone-600">Notes</label>
        <textarea value={f.notes} onChange={set("notes")} rows={2}
          className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="obs-notes" />
      </div>
      <label className="flex items-center gap-2 rounded-md border border-stone-200 bg-white p-2.5" data-testid="obs-push-row">
        <input type="checkbox" checked={f.push_to_employee} onChange={(e) => setF((cur) => ({ ...cur, push_to_employee: e.target.checked }))} data-testid="obs-push-toggle" />
        <span className="text-xs text-stone-700"><strong>Send to the employee for acknowledgment.</strong> They'll see it in the app when they log in — no same-day signature needed; pending ones stay visible until acknowledged.</span>
      </label>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy || !f.face_to_face_done} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="obs-save">
          {busy ? "Saving…" : "Save record"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full" data-testid="obs-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export default function ObservationsPage() {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState("");

  const load = useCallback(() => {
    api.get("/admin/observations").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="mx-auto max-w-4xl p-6 text-sm text-stone-500">Loading…</div>;

  const printRec = async (id) => {
    try {
      const res = await api.get(`/admin/observations/${id}/print.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6" data-testid="observations-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Direct observations & face-to-face</h1>
        <p className="mt-1 text-sm text-stone-500">
          First visits prompt automatically from visit data. Also covers provisional hires and random observations.
        </p>
      </div>

      <div className="rounded-xl border border-[#C49A3A]/30 bg-white p-5 shadow-sm" data-testid="obs-prompts">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#8a6a14]">
          Needs answers ({(data.prompts || []).length}) — first visits in the last 45 days
        </p>
        {(data.prompts || []).length === 0 && <p className="mt-2 text-sm text-stone-500">Nothing waiting. New first visits will appear here automatically.</p>}
        <div className="mt-2 space-y-2">
          {(data.prompts || []).map((p) => {
            const key = `${p.client_id}|${p.caregiver_name}`;
            return (
              <div key={key} className="rounded-lg border border-stone-200 p-3" data-testid={`obs-prompt-${key.replace(/\W+/g, "-")}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-stone-800">
                    <strong>{p.caregiver_name}</strong> — first visit with <strong>{p.client_name}</strong> on {p.first_visit_date}
                  </p>
                  <div className="flex gap-2">
                    <Link to={`/clients/${p.client_id}`} className="text-xs font-semibold text-primary hover:underline">Open client</Link>
                    <button onClick={() => setOpenKey(openKey === key ? "" : key)}
                      className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-stone-900"
                      data-testid={`obs-answer-${key.replace(/\W+/g, "-")}`}>
                      {openKey === key ? "Close" : "Answer"}
                    </button>
                  </div>
                </div>
                {openKey === key && <ObservationForm target={p} onSaved={() => { setOpenKey(""); load(); }} onCancel={() => setOpenKey("")} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="obs-recorded">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Recorded ({(data.recorded || []).length})</p>
        <div className="mt-2 space-y-2">
          {(data.recorded || []).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-100 p-3" data-testid={`obs-record-${r.id}`}>
              <div>
                <p className="text-sm font-semibold text-stone-800">{r.caregiver_name} · {r.observation_type}</p>
                <p className="text-xs text-stone-500">
                  Face-to-face: {r.face_to_face_done || "—"} · Provisional: {r.is_provisional || "—"}
                  {r.face_to_face_done === "No" && ` · Client feedback: ${r.client_feedback || "—"}`}
                  {" · "}{String(r.updated_at || "").slice(0, 10)}
                  {r.employee_ack && <> · <span className={r.employee_ack.status === "acknowledged" ? "font-semibold text-[#1e8449]" : "font-semibold text-[#8a5d14]"}>
                    {r.employee_ack.status === "acknowledged" ? `acknowledged ${String(r.employee_ack.acknowledged_at).slice(0, 10)}` : r.employee_ack.status === "pending" ? "acknowledgment pending" : "no employee login"}
                  </span></>}
                </p>
              </div>
              <button onClick={() => printRec(r.id)} className="rounded-full border border-stone-300 px-3 py-1 text-xs font-semibold text-stone-700 hover:bg-stone-50" data-testid={`obs-print-${r.id}`}>
                Print record
              </button>
            </div>
          ))}
          {(data.recorded || []).length === 0 && <p className="text-sm text-stone-500">No records yet.</p>}
        </div>
      </div>
    </div>
  );
}
